import { toDateKey } from '@/utils/capacityCalendar';

export type CalendarGranularity = 'day' | 'week' | 'month';

export interface CalendarBucket {
  key: string;
  label: string;
  startDate: Date;
  endDate: Date;
  isCurrent: boolean;
}

// Deliberately narrow: a ±2y span made the horizontal scroll disorienting (Aug 24
// review). 6 months of history covers the planned backfill; widen here if that grows.
const CALENDAR_MONTHS_BEFORE = 6;
const CALENDAR_MONTHS_AFTER = 12;
const DAYS_PER_WEEK = 7;
const MILLISECONDS_PER_DAY = 86_400_000;
// Labels are pinned to en-US so header copy and tests are deterministic across machines.
const LABEL_LOCALE = 'en-US';

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Calendar-component arithmetic: the Date constructor normalizes
// out-of-range day values (including across DST transitions), so this
// always yields local midnight on the target day rather than drifting
// by an hour when a DST boundary falls within the span.
function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function daysBetweenInclusive(start: Date, end: Date): number {
  const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((endUtc - startUtc) / MILLISECONDS_PER_DAY) + 1;
}

function weeksBetweenInclusive(startWeek: Date, endWeek: Date): number {
  return Math.floor(daysBetweenInclusive(startWeek, endWeek) / DAYS_PER_WEEK) + 1;
}

function monthsBetweenInclusive(startMonth: Date, endMonth: Date): number {
  return (endMonth.getFullYear() - startMonth.getFullYear()) * 12 + (endMonth.getMonth() - startMonth.getMonth()) + 1;
}

function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  // Monday-based week: getDay() is 0 for Sunday.
  const offset = (day.getDay() + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
  return addDays(day, -offset);
}

// The Monday of the week containing `today`, as a bucket key.
function currentWeekKey(today: Date = new Date()): string {
  return toDateKey(startOfWeek(today));
}

// Week keys are Mondays in ISO order, so comparing them as strings compares the dates. The
// current week is not past — it is being worked now, and which day of it we are on is nobody's
// business here.
export function isPastWeekKey(weekKey: string, today: Date = new Date()): boolean {
  return weekKey < currentWeekKey(today);
}

function shortDate(date: Date): string {
  return date.toLocaleDateString(LABEL_LOCALE, { month: 'short', day: 'numeric' });
}

function shortDateWithYear(date: Date): string {
  return date.toLocaleDateString(LABEL_LOCALE, { month: 'short', day: 'numeric', year: 'numeric' });
}

function dayLabel(date: Date): string {
  const weekday = date.toLocaleDateString(LABEL_LOCALE, { weekday: 'short' });
  return `${weekday}\n${shortDateWithYear(date)}`;
}

function contains(bucket: { startDate: Date; endDate: Date }, date: Date): boolean {
  return date >= bucket.startDate && date <= bucket.endDate;
}

// Month-granular either side of today, like every other range here: `monthsBefore` of 1 means
// "from the 1st of last month", snapped back to that week's Monday. Separately exported so the
// scheduling board can hold a narrower history than the calendar and widen it on demand.
export function generateWeekBuckets(monthsBefore: number, monthsAfter: number, today: Date = new Date()): CalendarBucket[] {
  const now = startOfDay(today);
  const first = startOfWeek(new Date(now.getFullYear(), now.getMonth() - monthsBefore, 1));
  const last = startOfWeek(new Date(now.getFullYear(), now.getMonth() + monthsAfter + 1, 0));
  return Array.from({ length: weeksBetweenInclusive(first, last) }, (_, index) => {
    const startDate = addDays(first, index * DAYS_PER_WEEK);
    // Next bucket's local midnight, minus 1ms, for an inclusive end
    // that stays correct across DST transitions.
    const endDate = new Date(addDays(startDate, DAYS_PER_WEEK).getTime() - 1);
    // Last day of the week, derived via component arithmetic (not by
    // subtracting ms from endDate), so the label is DST-safe too.
    const lastDayOfWeek = addDays(startDate, DAYS_PER_WEEK - 1);
    return {
      key: toDateKey(startDate),
      label: `${shortDate(startDate)} – ${shortDateWithYear(lastDayOfWeek)}`,
      startDate,
      endDate,
      isCurrent: contains({ startDate, endDate }, now),
    };
  });
}

// How far either side of today generateWeekBuckets has to reach to hold this week: the
// month gap between the key's Monday and today, as a monthsBefore or a monthsAfter. The
// board widens to this on a jump to a week it is not showing. Measured on the Monday because
// that is what keys the column — a week that straddles a month end belongs to the earlier one.
export function monthsSpanningWeek(weekKey: string, today: Date = new Date()): { before: number; after: number } {
  const [year, month] = weekKey.split('-').map(Number);
  const monday = new Date(year, month - 1, 1);
  const gap = monthsBetweenInclusive(new Date(today.getFullYear(), today.getMonth(), 1), monday) - 1;
  return gap < 0 ? { before: -gap, after: 0 } : { before: 0, after: gap };
}

export function generateBuckets(granularity: CalendarGranularity, today: Date = new Date()): CalendarBucket[] {
  const now = startOfDay(today);

  if (granularity === 'day') {
    const first = new Date(now.getFullYear(), now.getMonth() - CALENDAR_MONTHS_BEFORE, 1);
    const last = new Date(now.getFullYear(), now.getMonth() + CALENDAR_MONTHS_AFTER + 1, 0);
    return Array.from({ length: daysBetweenInclusive(first, last) }, (_, index) => {
      const startDate = addDays(first, index);
      // Next bucket's local midnight, minus 1ms, for an inclusive end
      // that stays correct across DST transitions.
      const endDate = new Date(addDays(startDate, 1).getTime() - 1);
      return {
        key: toDateKey(startDate),
        label: dayLabel(startDate),
        startDate,
        endDate,
        isCurrent: contains({ startDate, endDate }, now),
      };
    });
  }

  if (granularity === 'week') {
    return generateWeekBuckets(CALENDAR_MONTHS_BEFORE, CALENDAR_MONTHS_AFTER, today);
  }

  const firstMonth = new Date(now.getFullYear(), now.getMonth() - CALENDAR_MONTHS_BEFORE, 1);
  const lastMonth = new Date(now.getFullYear(), now.getMonth() + CALENDAR_MONTHS_AFTER, 1);
  return Array.from({ length: monthsBetweenInclusive(firstMonth, lastMonth) }, (_, index) => {
    const startDate = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + index, 1);
    const endDate = new Date(new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1).getTime() - 1);
    return {
      key: toDateKey(startDate),
      label: startDate.toLocaleDateString(LABEL_LOCALE, { month: 'long', year: 'numeric' }),
      startDate,
      endDate,
      isCurrent: contains({ startDate, endDate }, now),
    };
  });
}
